import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ExperimentFlags, ExperimentValue } from '../helpers/amplitude'

const AB_TESTS_COOKIE = 'ABTests'
const AB_TESTS_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export type ExperimentAssignments = Partial<Record<ExperimentFlags, ExperimentValue>> & Record<string, ExperimentValue>

interface ExperimentsContextValue {
  assignments: ExperimentAssignments
  setVariant: (flag: string, variant: ExperimentValue) => void
  getVariant: (flag: string, fallback: ExperimentValue) => ExperimentValue
}

const ExperimentsContext = createContext<ExperimentsContextValue>({
  assignments: {},
  setVariant: () => {},
  getVariant: (_flag, fallback) => fallback,
})

function parseABTestsCookie(cookieString: string): ExperimentAssignments {
  const cookieEntry = cookieString
    .split('; ')
    .find((entry) => entry.startsWith(`${AB_TESTS_COOKIE}=`))
    ?.slice(AB_TESTS_COOKIE.length + 1)

  if (!cookieEntry) {
    return {}
  }

  try {
    const decoded = decodeURIComponent(cookieEntry)
    const parsed = JSON.parse(decoded)
    if (!parsed || typeof parsed !== 'object') {
      return {}
    }

    return Object.entries(parsed).reduce<ExperimentAssignments>((acc, [key, value]) => {
      if (
        (Object.values(ExperimentFlags) as string[]).includes(key) &&
        (value === ExperimentValue.Control || value === ExperimentValue.Treatment)
      ) {
        acc[key] = value
      }
      return acc
    }, {})
  } catch (error) {
    return {}
  }
}

function writeABTestsCookie(assignments: ExperimentAssignments) {
  const serialized = encodeURIComponent(JSON.stringify(assignments))
  document.cookie = `${AB_TESTS_COOKIE}=${serialized}; Path=/; Max-Age=${AB_TESTS_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
}

export function ExperimentsProvider({ children }: { children: React.ReactNode }) {
  const [assignments, setAssignments] = useState<ExperimentAssignments>({})

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    setAssignments(parseABTestsCookie(document.cookie))
  }, [])

  const setVariant = useCallback((flag: string, variant: ExperimentValue) => {
    setAssignments((previous) => {
      const next = { ...previous, [flag]: variant }
      if (typeof document !== 'undefined') {
        writeABTestsCookie(next)
      }
      return next
    })
  }, [])

  const getVariant = useCallback(
    (flag: string, fallback: ExperimentValue) => assignments[flag] ?? fallback,
    [assignments]
  )

  const value = useMemo(
    () => ({
      assignments,
      setVariant,
      getVariant,
    }),
    [assignments, setVariant, getVariant]
  )

  return <ExperimentsContext.Provider value={value}>{children}</ExperimentsContext.Provider>
}

export function useExperimentsContext() {
  return useContext(ExperimentsContext)
}
